import { useEffect, useState } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router'
import { deleteMeta, getMeta } from '../state/db.js'
import { useChildStore } from '../state/store.js'
import { log, reason } from './log.js'
import { ScreenState } from './ScreenState.js'

const logger = log('guards')

/**
 * CONTRACTS §7 guards. The child guard needs `meta.activeChildId`; the parent guard needs a
 * capability that is under five minutes old and is cleared when the app hides (CONTRACTS §9).
 * WP-F builds the PIN and arithmetic screen behind `/veli`; this only enforces the rule so no
 * lane has to remember it.
 */

export function RequireChild({ children }: { children: React.ReactNode }) {
  const { childId, status } = useChildStore()
  const [params] = useSearchParams()
  // The screenshot and recording harness enters a child route directly with `?seed=` (dev
  // only, app/src/app/devSeed.ts). Redirecting to /profil would drop the query before the seed
  // ran, so the guard waits one beat for the seeded profile instead.
  const seeding = import.meta.env.DEV && params.get('seed') !== null && !childId

  // Only the first load has nothing to show. Once a child is active the guard keeps rendering
  // the screen through every later `status: 'loading'` (the store re-folds after every append),
  // because swapping in a skeleton there would unmount the screen and lose its local state:
  // a lesson would jump back to card one and a quiz would forget the attempt.
  if (!childId && (status === 'idle' || status === 'loading' || seeding)) {
    return (
      <ScreenState status="loading" onRetry={() => window.location.reload()}>
        {null}
      </ScreenState>
    )
  }
  if (!childId) return <Navigate to="/profil" replace />
  return <>{children}</>
}

/** null while the capability is still being read from Dexie. */
export function useParentGate(): boolean | null {
  const [fresh, setFresh] = useState<boolean | null>(null)

  useEffect(() => {
    let cancelled = false
    const check = () =>
      getMeta<number>('parentGateUntil').then(
        (until) => {
          if (!cancelled) setFresh((until ?? 0) > Date.now())
        },
        (error: unknown) => {
          // An unreadable capability is a closed gate, never a crash on /veli.
          logger.warn('the parent gate could not be read', reason(error))
          if (!cancelled) setFresh(false)
        },
      )
    void check()

    // Hiding the app clears the capability, and a five minute window needs re checking while
    // the parent stays on the screen.
    const onVisibility = () => {
      if (document.visibilityState !== 'hidden') {
        void check()
        return
      }
      setFresh(false)
      void deleteMeta('parentGateUntil').catch((error: unknown) =>
        logger.warn('the parent gate could not be closed', reason(error)),
      )
    }
    document.addEventListener('visibilitychange', onVisibility)
    const timer = setInterval(check, 30_000)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisibility)
      clearInterval(timer)
    }
  }, [])

  return fresh
}

export function RequireParentGate({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const fresh = useParentGate()

  if (fresh === null) {
    return (
      <ScreenState status="loading" onRetry={() => window.location.reload()}>
        {null}
      </ScreenState>
    )
  }
  if (!fresh) return <Navigate to="/veli" replace state={{ from: location.pathname }} />
  return <>{children}</>
}
